import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { FiPlusCircle, FiArrowRight, FiSettings } from 'react-icons/fi';
import { Header, CustomPagination } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';
import SkeletonTable from '../../components/common/SkeletonTable';
import FundSelector from '../../components/Finance/FundSelector';
import CreateSalaryBatchModal from '../../components/Finance/CreateSalaryBatchModal';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const statusMeta = {
  successful:           { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Successful' },
  partially_successful: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Partially Successful' },
  failed:                { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Failed' },
  needs_review:          { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Needs Review' },
  processing:            { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Processing' },
  pending:               { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Pending' },
};

const SalaryBatches = () => {
  useKYCStatusRedirect('/finance/payroll/batches');
  const history = useHistory();
  const { school } = useStateContext();
  const schoolId = school?._id;
  const multiFundEnabled = !!school?.settings?.finance?.multi_fund_enabled;

  const [batches, setBatches] = useState([]);
  const [fundId, setFundId] = useState('');
  const [paymentMonth, setPaymentMonth] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const fetchBatches = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fundId) params.set('fund_id', fundId);
      if (paymentMonth) params.set('payment_month', paymentMonth);
      if (status) params.set('status', status);
      const { data } = await userRequest.get(`/schools/${schoolId}/salary-batches?${params.toString()}`);
      setBatches(Array.isArray(data) ? data : (data.batches || []));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, fundId, paymentMonth, status]);

  const columns = [
    {
      name: 'Payment Month',
      cell: row => <span className="font-medium text-gray-800">{row.payment_month}</span>,
    },
    {
      name: 'Fund',
      cell: row => <span className="text-sm text-gray-600">{row.fund?.name || row.fund_id}</span>,
    },
    {
      name: 'Recipients',
      cell: row => <span className="text-sm text-gray-600">{row.recipient_count}</span>,
    },
    {
      name: 'Total Amount',
      cell: row => <span className="font-semibold text-gray-800">{formatCurrency(row.total_amount)}</span>,
    },
    {
      name: 'Status',
      cell: row => {
        const s = statusMeta[row?.status] || statusMeta.pending;
        return (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${s.bg} ${s.text}`}>
            {s.label}
          </span>
        );
      },
    },
    {
      cell: row => (
        <Link to={`/finance/payroll/batches/${row._id}`}>
          <button className="p-2 rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
            <FiArrowRight className="w-4 h-4" />
          </button>
        </Link>
      ),
      ignoreRowClick: true,
      button: true,
      width: '56px',
    },
  ];

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex items-start justify-between mb-6">
        <Header
          category="Finance"
          title="Salary Batches"
          description="Prepare payroll, review the final debit, and track every staff payout safely."
        />
        <div className="flex items-center gap-2">
          <Link to="/finance/payroll/setup">
            <button className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors">
              <FiSettings className="w-4 h-4" />
              Set Salaries
            </button>
          </Link>
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors"
          >
            <FiPlusCircle className="w-4 h-4" />
            New Batch
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-48">
          <FundSelector schoolId={schoolId} value={fundId} onChange={setFundId} includeAllOption />
        </div>
        <input
          type="month"
          value={paymentMonth}
          onChange={e => setPaymentMonth(e.target.value)}
          className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-4 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-4 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="successful">Successful</option>
          <option value="partially_successful">Partially Successful</option>
          <option value="failed">Failed</option>
          <option value="needs_review">Needs Review</option>
        </select>
      </div>

      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <DataTable
          data={batches}
          columns={columns}
          getRowId={row => row._id}
          onRowClicked={row => history.push(`/finance/payroll/batches/${row._id}`)}
          pointerOnHover
          pagination
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={6} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-14 text-center text-sm text-gray-400">No salary batches found.</div>
          }
        />
      </div>

      {isCreateOpen && (
        <CreateSalaryBatchModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          schoolId={schoolId}
          onCreated={(batch) => {
            setIsCreateOpen(false);
            fetchBatches();
            if (batch?._id) history.push(`/finance/payroll/batches/${batch._id}`);
          }}
        />
      )}
    </div>
  );
};

export default SalaryBatches;
